// NeuMenue.tsx — das „+“-Menü im App-Header: acht Nomen als schlichte Liste
// untereinander (User 28.09.2026: „die Punkte bei + bitte untereinander wie in
// einer Liste"; bis dahin 2×4-Kacheln).
//
// Eigenes Popover statt Fluent-Popover (PO 28.09.2026: „kein Fluent-Popup,
// sondern nach eigenem Design"). Portal an `body`, `position: fixed` unter dem
// Knopf, rechtsbündig. Schließt über Esc, Klick daneben und nach der Wahl.
//
// Tastatur: Pfeil hoch/runter bewegt ringförmig durch die Liste, Pos1/Ende
// springen an den Rand, Enter/Leertaste wählt (nativer Button), Tab
// schließt. Beim Öffnen liegt der Fokus auf dem ersten Eintrag, beim
// Schließen wieder auf dem „+“.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import {
  Add20Regular,
  CalendarLtr20Regular,
  Clipboard20Regular,
  Document20Regular,
  Mail20Regular,
  People20Regular,
  Shield20Regular,
  TaskListLtr20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import type { NeuAnlageArt, NeuAnlageEintrag } from "./neuAnlageEintraege";
import "./neuAnlage.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

const SYMBOL: Record<NeuAnlageArt, React.ReactElement> = {
  kontakt: <People20Regular />,
  risiko: <Shield20Regular />,
  vertrag: <Clipboard20Regular />,
  schaden: <Warning20Regular />,
  vorgang: <TaskListLtr20Regular />,
  termin: <CalendarLtr20Regular />,
  mail: <Mail20Regular />,
  dokument: <Document20Regular />,
};

const BREITE = 240;
const RAND = 12;

export function NeuMenue({
  eintraege,
  onWaehlen,
  isPhone = false,
  knopfStyle,
}: {
  eintraege: readonly NeuAnlageEintrag[];
  onWaehlen: (art: NeuAnlageArt) => void;
  isPhone?: boolean;
  knopfStyle?: React.CSSProperties;
}) {
  const [offen, setOffen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const knopfRef = React.useRef<HTMLButtonElement>(null);
  const menueRef = React.useRef<HTMLDivElement>(null);
  const eintragRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const platzieren = React.useCallback(() => {
    const el = knopfRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    if (isPhone || vw < BREITE + 2 * RAND) {
      setPos({ top: r.bottom + 8, left: RAND, width: vw - 2 * RAND });
      return;
    }
    const left = Math.max(RAND, Math.min(r.right - BREITE, vw - BREITE - RAND));
    setPos({ top: r.bottom + 8, left, width: BREITE });
  }, [isPhone]);

  const schliessen = React.useCallback((fokusZurueck = true) => {
    setOffen(false);
    if (fokusZurueck) knopfRef.current?.focus();
  }, []);

  const umschalten = () => {
    if (offen) {
      schliessen();
      return;
    }
    platzieren();
    setOffen(true);
  };

  // Fokus auf den ersten Eintrag, sobald das Menü steht.
  React.useEffect(() => {
    if (offen && pos) eintragRefs.current[0]?.focus();
    // Nur beim Öffnen — nicht bei jedem Neu-Platzieren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, pos !== null]);

  React.useEffect(() => {
    if (!offen) return;
    const beiKlick = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menueRef.current?.contains(t) || knopfRef.current?.contains(t)) return;
      schliessen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        schliessen();
      }
    };
    alleFenster.addEventListener("pointerdown", beiKlick);
    alleFenster.addEventListener("keydown", beiTaste);
    alleFenster.addEventListener("resize", platzieren);
    return () => {
      alleFenster.removeEventListener("pointerdown", beiKlick);
      alleFenster.removeEventListener("keydown", beiTaste);
      alleFenster.removeEventListener("resize", platzieren);
    };
  }, [offen, schliessen, platzieren]);

  const beiPfeil = (e: React.KeyboardEvent, i: number) => {
    const n = eintraege.length;
    let ziel: number | null = null;
    // Eine Liste untereinander (User 28.09.2026): hoch/runter, ringförmig.
    if (e.key === "ArrowDown") ziel = (i + 1) % n;
    else if (e.key === "ArrowUp") ziel = (i - 1 + n) % n;
    else if (e.key === "Home") ziel = 0;
    else if (e.key === "End") ziel = n - 1;
    else if (e.key === "Tab") {
      schliessen(false);
      return;
    }
    if (ziel === null) return;
    e.preventDefault();
    eintragRefs.current[ziel]?.focus();
  };

  if (eintraege.length === 0) return null;

  return (
    <>
      <button
        ref={knopfRef}
        type="button"
        aria-label="Neu anlegen"
        title="Neu anlegen"
        aria-haspopup="menu"
        aria-expanded={offen}
        style={knopfStyle}
        onClick={umschalten}
      >
        <Add20Regular />
      </button>
      {offen && pos
        ? createPortal(
            <div
              ref={menueRef}
              className="na-menu"
              role="menu"
              aria-label="Neu anlegen"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
            >
              <div className="na-menu-kopf" aria-hidden>
                Neu anlegen
              </div>
              <div className="na-menu-liste">
                {eintraege.map((e, i) => (
                  <button
                    key={e.art}
                    ref={(el) => {
                      eintragRefs.current[i] = el;
                    }}
                    type="button"
                    role="menuitem"
                    className="na-menu-eintrag"
                    tabIndex={i === 0 ? 0 : -1}
                    onKeyDown={(ev) => beiPfeil(ev, i)}
                    onClick={() => {
                      schliessen(false);
                      onWaehlen(e.art);
                    }}
                  >
                    <span className="na-menu-ico" aria-hidden>
                      {SYMBOL[e.art]}
                    </span>
                    {e.label}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export default NeuMenue;
